import './Select.scss';
import { SelectOption } from '../../core/types';
import {
  KeyboardEventHandler,
  MouseEventHandler,
  useEffect,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { useOnClickOutside, useDebounce } from '../../hooks';
import { ReactComponent as PolygonIcon } from '../../icons/chevron-right.svg';
import { ReactComponent as PolygonSmIcon } from '../../icons/select-chevron.svg';
import { useTranslation } from 'react-i18next';
import SimpleBar from 'simplebar-react';

interface Props {
  options: SelectOption[];
  value?: number | string;
  onChange: (v: string | number) => void;
  onBlur?: () => void;
  placeholder?: string;
  className?: string;
  label?: string;
  error?: string;
  touched?: boolean;
  disabled?: boolean;
  isOpenAbove?: boolean;
  name?: string;
  size?: 'lg' | 'sm' | 'md';
}

const getActiveOption = (value: string | number, options: SelectOption[]) =>
  options.find((option) => value === option.id);

export const Select = ({
  options,
  value,
  placeholder = 'Please select',
  onChange,
  onBlur,
  className = '',
  name,
  label,
  error,
  touched,
  disabled = false,
  isOpenAbove,
  size = 'md',
}: Props) => {
  const { t } = useTranslation();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<any>(null);
  const ref = useRef<HTMLButtonElement>(null);
  const [isOpened, setIsOpened] = useState(false);
  const checkedOption = value ? getActiveOption(value, options) : undefined;
  const debouncedIsOpened = useDebounce(isOpened, 250);
  const isDropdownOpened = isOpened || debouncedIsOpened;
  const [activeIndex, setActiveIndex] = useState(
    options.findIndex((o) => o.id === value)
  );

  const content: string | JSX.Element = checkedOption
    ? checkedOption.content || t(checkedOption.name)
    : t(placeholder);

  const onClickOuside = () => {
    if (!isOpened) return;
    setIsOpened(false);
    onBlur && onBlur();
  };

  useOnClickOutside(ref, onClickOuside);

  const onClick: MouseEventHandler<HTMLButtonElement> = () => {
    if (disabled) return;
    setIsOpened(!isOpened);
  };

  const handleBlur = () => {
    if (disabled) return;
    if (onBlur) onBlur();
  };

  const onSelect = (optionId: string | number) => () => {
    setIsOpened(false);
    onChange(optionId);
  };

  useEffect(() => {
    if (isOpened && activeIndex !== -1 && menuRef.current) {
      menuRef.current.el.focus();
      const contentWrapper = menuRef.current.getContentElement();
      const targetEl = contentWrapper.children[activeIndex];
      const targetRect = targetEl?.getBoundingClientRect();
      const containerRect = menuRef.current.el
        .querySelector('.simplebar-content-wrapper')
        .getBoundingClientRect();

      if (
        targetRect &&
        containerRect &&
        activeIndex <= options.length - 1 &&
        (targetRect.bottom > containerRect.bottom ||
          targetRect.top < containerRect.top)
      ) {
        targetEl.scrollIntoView();
      }
    }

    if (!isOpened) {
      setActiveIndex(-1);
    }
  }, [isOpened, activeIndex, options]);

  const onKeyDown: KeyboardEventHandler<HTMLButtonElement> = (event) => {
    switch (event.key) {
      case ' ':
      case 'Enter':
        if (isOpened && activeIndex !== -1 && options[activeIndex].id) {
          onSelect(options[activeIndex].id)();
          break;
        }
        return;
      case 'Escape':
        if (isOpened) setIsOpened(false);
        break;
      case 'ArrowUp':
        if (isOpened) {
          if (activeIndex > 0) {
            setActiveIndex((s) => s - 1);
          } else {
            setActiveIndex(options.length - 1);
          }
        }
        break;
      case 'ArrowDown':
        if (isOpened) {
          if (activeIndex !== -1 && activeIndex < options.length - 1) {
            setActiveIndex((s) => s + 1);
          } else {
            setActiveIndex(0);
          }
        }
        break;

      default:
        return;
    }
    event.preventDefault();
  };

  return (
    <div
      className={classNames('Select', className, {
        'Select--lg': size === 'lg',
        'Select--sm': size === 'sm',
      })}
    >
      {!!label && <div className='Select__label'>{t(label)}</div>}
      <button
        data-name={name}
        data-value={value}
        data-testid={name}
        ref={ref}
        type='button'
        className={classNames(
          'Select__input d-flex justify-content-between align-items-center',
          {
            'Select__input--active': isOpened,
            'Select__input--disabled': disabled,
            'Select__input--invalid': touched && error,
          }
        )}
        onClick={onClick}
        onBlur={handleBlur}
        onKeyDown={onKeyDown}
      >
        <span
          className={classNames({
            Select__value: value,
            Select__placeholder: !value,
          })}
        >
          {content}
        </span>
        {size === 'lg' ? (
          <PolygonIcon className='Select__arrow' />
        ) : (
          <PolygonSmIcon className='Select__arrow' />
        )}
        {isDropdownOpened && (
          <div
            className={classNames('Select__dropdown', {
              fadeIn: isOpened,
              fadeOut: !isOpened,
              'Select__dropdown--up': isOpenAbove,
            })}
            ref={dropdownRef}
          >
            <SimpleBar
              style={{ maxHeight: 220 }}
              ref={menuRef}
              autoHide={false}
              className='Select__scrollbar'
            >
              {options.map((o, i) => (
                <div
                  style={{ position: 'relative', zIndex: 1000 }}
                  className={classNames('Select__dropdownItem text-start', {
                    'Select__dropdownItem--active': activeIndex === i,
                  })}
                  onClick={onSelect(o.id)}
                  key={o.id}
                >
                  {o.content || t(o.name)}
                </div>
              ))}
            </SimpleBar>
          </div>
        )}
      </button>
      {touched && error && (
        <div className='Select__error' data-testid={`${name}-error`}>
          {t(error)}
        </div>
      )}
    </div>
  );
};
